import React from 'react';
import './App.css';
import logoWhite from './assets/logo_main_white.png';
import { formatRange, HOURS, useBarStatus } from './data/hours';
import { LIVE_MENU_URL, Roaster, TAPROOM_MENU_URL, useCoffeeBoard } from './data/coffeeBoard';
import { PAST_ROASTERS, PastRoaster, roasterSite } from './data/pastRoasters';

const ADDRESS = '1409 NE 80th St, Seattle, WA';
const DIRECTIONS_URL = 'https://www.google.com/maps/search/?api=1&query=Full+Tank+Coffee+1409+NE+80th+St+Seattle+WA';

const StatusPill = () => {
  const status = useBarStatus();
  return (
    <span className={`pill ${status.isOpen ? 'open' : 'closed'}`}>
      <span className="dot" />
      {status.label}
    </span>
  );
};

const Nav = () => {
  const [open, setOpen] = React.useState(false);
  const links = (
    <>
      <a href="#menu" onClick={() => setOpen(false)}>Menu</a>
      <a href="#roasters" onClick={() => setOpen(false)}>Roasters</a>
      <a href="#visit" onClick={() => setOpen(false)}>Visit</a>
    </>
  );
  return (
    <header className="nav">
      <div className="wrap">
        <a href="#top" aria-label="Full Tank Coffee, home">
          <img src={logoWhite} alt="Full Tank Coffee" className="nav-logo" />
        </a>
        <nav className="links">{links}</nav>
        <div className="nav-right">
          <StatusPill />
          <button className="burger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(!open)}>
            <span />
          </button>
        </div>
      </div>
      {open && <nav className="drawer">{links}</nav>}
    </header>
  );
};

const HoursCard = () => (
  <div className="hours">
    <div className="eyebrow muted">Coffee bar hours</div>
    {HOURS.map((row) => (
      <div className="row" key={row.label}>
        <span>{row.label}</span>
        <b>{formatRange(row)}</b>
      </div>
    ))}
    <div className="addr">
      1409 NE 80th St, Seattle
      <br />
      Inside Project 9 Brewing
    </div>
  </div>
);

/** "Naomi Joe Coffee Roasters" → "Naomi Joe"; "Tacoma, WA" → "Tacoma". */
const shortName = (name: string) =>
  name.replace(/\s+(coffee\s+)?(roasters|roastery|roasting\s+co\.?|company|co\.?)$/i, '').replace(/\s+coffee$/i, '').trim();
const city = (location?: string) => location?.split(',')[0].trim();

/**
 * The headline names whoever is on the bar, read live from the coffee board,
 * so every visit shows the rotation. While it loads, the line holds its space
 * so nothing jumps; with no featured roaster set, it falls back to the search.
 */
const Headline = ({ roaster }: { roaster?: Roaster | null }) => {
  if (roaster === null) {
    return (
      <h1>
        Always in search of
        <br />
        <b>the best beans.</b>
      </h1>
    );
  }
  const place = city(roaster?.location);
  return (
    <h1>
      Now pouring
      <br />
      <b className={roaster ? undefined : 'pending'}>
        {roaster ? `${shortName(roaster.name)}${place ? `, ${place}` : ''}.` : 'Loading.'}
      </b>
    </h1>
  );
};

const Hero = ({ roaster }: { roaster?: Roaster | null }) => (
  <section className="hero" id="top">
    <div className="wrap">
      <div className="hero-copy">
        <div className="eyebrow crema">Coffee bar at Project 9 Brewing · Seattle</div>
        <Headline roaster={roaster} />
        <p>
          Every few weeks a different roaster takes over our bar, from Seattle favorites to small-batch roasters around
          the world. Whole beans on the shelf to take home.
        </p>
        <div className="ctas">
          <a className="btn solid" href="#menu">
            See today's menu ↓
          </a>
          <a className="btn ghost" href={DIRECTIONS_URL} target="_blank" rel="noreferrer">
            Get directions
          </a>
        </div>
      </div>
      <HoursCard />
    </div>
  </section>
);

const NowPouring = ({ roaster }: { roaster?: Roaster | null }) => {
  if (roaster === null) {
    return null;
  }
  if (!roaster) {
    return <section className="wrap roaster loading" aria-busy="true" />;
  }
  return (
    <section className="wrap roaster">
      <div>
        {roaster.logo && <img className="rlogo" src={roaster.logo} alt="" />}
        <div className="eyebrow crema">Meet the roaster</div>
        <h2>{roaster.name}</h2>
        {roaster.location && <div className="loc">{roaster.location}</div>}
        {roaster.blurb && <p>{roaster.blurb}</p>}
        {roaster.since && <div className="since">{roaster.since}</div>}
        {roasterSite(roaster.name) && (
          <a className="site" href={roasterSite(roaster.name)} target="_blank" rel="noreferrer">
            Visit {shortName(roaster.name)} ↗
          </a>
        )}
      </div>
      <div className="bags">
        {roaster.bags.map((bag) => (
          <div className="bag" key={bag.id}>
            {bag.images?.[0] && <img src={bag.images[0]} alt={bag.name} loading="lazy" />}
            <h4>{bag.name}</h4>
            {bag.description && <span>{bag.description.charAt(0).toUpperCase() + bag.description.slice(1)}</span>}
          </div>
        ))}
      </div>
    </section>
  );
};

const Rotation = () => (
  <div className="wrap">
    <div className="steps">
      <div>
        <div className="n">01</div>
        <h3>We taste a lot of coffee.</h3>
        <p>We're always trying roasters, from shops here in Seattle to small-batch roasters around the world.</p>
      </div>
      <div>
        <div className="n">02</div>
        <h3>One roaster at a time.</h3>
        <p>Each one takes over our espresso for a few weeks, sometimes longer, so you can really get to know them.</p>
      </div>
      <div>
        <div className="n">03</div>
        <h3>Take it home.</h3>
        <p>Whole beans from whoever's on the bar are on the shelf, ready to go.</p>
      </div>
    </div>
  </div>
);

const RoasterLogo = ({ roaster }: { roaster: PastRoaster }) => (
  <a className="logo" href={roaster.url} target="_blank" rel="noreferrer" aria-label={`${roaster.name}, ${roaster.location}`}>
    <span className="mark">
      <img className={roaster.size ?? 'wide'} src={roaster.logo} alt="" loading="lazy" />
      {roaster.label && <small>{roaster.name}</small>}
    </span>
    <span className="from">{roaster.location}</span>
  </a>
);

const PastRoasters = ({ current }: { current?: string }) => {
  const key = current?.split(' ')[0].toLowerCase();
  const past = PAST_ROASTERS.filter((roaster) => !key || !roaster.name.toLowerCase().startsWith(key));
  return (
    <section className="past" id="roasters">
      <div className="wrap">
        <div className="sectionhead">
          <div>
            <div className="eyebrow crema">Past roasters</div>
            <h2>Roasters we're proud to have in the rotation</h2>
          </div>
          <p>A few of the roasters who've had the bar before.</p>
        </div>
        <div className="logos">
          {past.map((roaster) => (
            <RoasterLogo key={roaster.name} roaster={roaster} />
          ))}
        </div>
      </div>
    </section>
  );
};

/** Pastry photos shown in the case row; the rest of the case stays off the page. */
const PASTRY_LIMIT = 8;

const Menu = ({ board }: { board?: ReturnType<typeof useCoffeeBoard> }) => (
  <section className="menu-section" id="menu">
    <div className="wrap">
      <div className="sectionhead">
        <div>
          <div className="eyebrow crema">Menu</div>
          <h2>What we're pouring</h2>
        </div>
        {board?.roaster && (
          <span className="chip">
            {board.roaster.logo && <img src={board.roaster.logo} alt="" />}
            <span>
              Espresso from <b>{shortName(board.roaster.name)}</b>
            </span>
          </span>
        )}
      </div>
      {board === undefined && <div className="menu loading" aria-busy="true" />}
      {board === null && (
        <p className="menu-fallback">
          The menu didn't load. <a href={LIVE_MENU_URL}>See it on the Project 9 site.</a>
        </p>
      )}
      {board && (
        <div className="menu">
          {board.sections.map((section) => (
            <div key={section.id}>
              <h5>{section.name}</h5>
              <ul>
                {section.items.map((item) => (
                  <li key={item.id}>
                    {item.name}
                    {item.description && <small>{item.description}</small>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
      {!!board?.pastries.length && (
        <div className="strip">
          <div className="striphead">
            <h3>From the case</h3>
            <span>Pastries</span>
          </div>
          <div className="cards">
            {board.pastries.slice(0, PASTRY_LIMIT).map((pastry) => (
              <div className="card" key={pastry.id}>
                <img src={pastry.images![0]} alt={pastry.name} loading="lazy" />
                <h4>{pastry.name}</h4>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="menufoot">
        <span>Drinks can be made iced. Oat milk available.</span>
        <span>
          Beer, wine and food:{' '}
          <a href={TAPROOM_MENU_URL} target="_blank" rel="noreferrer">
            Project 9 taproom menu ↗
          </a>
        </span>
      </div>
    </div>
  </section>
);

const Visit = () => (
  <div className="wrap visit-wrap" id="visit">
    <div className="visit">
      <div className="ph" role="img" aria-label="Espresso pouring from the portafilter" />
      <div className="tx">
        <div className="eyebrow crema">Visit</div>
        <h2>Find us inside Project 9 Brewing</h2>
        <table>
          <tbody>
            {HOURS.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{formatRange(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p>{ADDRESS}. After 1 PM, cold brew and nitro latte keep pouring at the taproom bar until close.</p>
        <div className="ctas">
          <a className="btn solid" href={DIRECTIONS_URL} target="_blank" rel="noreferrer">
            Get directions
          </a>
        </div>
      </div>
    </div>
  </div>
);

const Footer = () => (
  <footer>
    <div className="wrap">
      <img src={logoWhite} alt="Full Tank Coffee" />
      <span>
        {ADDRESS} · A <a href="https://www.project9brewing.com">Project 9 Brewing</a> coffee bar
      </span>
    </div>
  </footer>
);

function App() {
  const board = useCoffeeBoard();
  // Undefined while loading; null once loaded with no featured roaster, or if Project 9 can't be reached
  const roaster = board === undefined ? undefined : board?.roaster ?? null;
  return (
    <>
      <Nav />
      <Hero roaster={roaster} />
      <NowPouring roaster={roaster} />
      <Rotation />
      <PastRoasters current={board?.roaster?.name} />
      <Menu board={board} />
      <Visit />
      <Footer />
    </>
  );
}

export default App;
